import {useLocation as useLocationTanStack} from '@tanstack/react-router'
import {useNavigate, type NavigateOptionsWithPreventAutofocus, type URLSearchParamsInit} from './use-navigate'
import {useCallback, useMemo} from 'react'

/**
 * React Router-compatible useSearchParams implementation for TanStack Router.
 * The setter wraps the useNavigate hook defined above.
 */

export const useSearchParams = (): [
  URLSearchParams,
  (
    nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptionsWithPreventAutofocus,
  ) => void,
] => {
  const {pathname, searchStr} = useLocationTanStack()
  const navigate = useNavigate()

  const searchParams = useMemo(() => {
    return new URLSearchParams(searchStr)
  }, [searchStr])

  const setSearchParams = useCallback(
    (
      nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
      navigateOpts?: NavigateOptionsWithPreventAutofocus,
    ) => {
      const nextValue = typeof nextInit === 'function' ? nextInit(searchParams) : nextInit
      const newSearchParams = createSearchParams(nextValue)
      navigate(
        {
          pathname,
          search: newSearchParams.toString(),
        },
        navigateOpts,
      )
    },
    [navigate, pathname, searchParams],
  )

  return [searchParams, setSearchParams]
}

function createSearchParams(init?: URLSearchParamsInit): URLSearchParams {
  if (!init) return new URLSearchParams()
  if (typeof init === 'string') return new URLSearchParams(init)
  if (init instanceof URLSearchParams) return new URLSearchParams(init)
  if (Array.isArray(init)) return new URLSearchParams(init)

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, item)
      }
    } else {
      params.set(key, value)
    }
  }

  return params
}
